/**
 * useCensusRadioMapLayer — Capa MapLibre para radios censales 2022.
 *
 * @remarks
 * visible:     muestra/oculta polígonos (eye toggle).
 * interactive: activa click/hover/selección (power toggle).
 *              Si interactive=false, los handlers devuelven sin actuar.
 *
 * Esquema visual:
 *  - Fill muy bajo (#2FA4B5 @ 0.07) para todos — no confundir con basemap.
 *  - Outline distingue: completo = teal 0.8px | parcial = ámbar 1.2px.
 *  - Seleccionado: fill teal 0.40 + outline blanco 2.5px.
 *
 * Layers:
 *  - census-radios-fill    : fill de los polígonos
 *  - census-radios-outline : outline de los polígonos
 */

import { useCallback, useEffect, useRef } from 'react';
import type { GeoJSONSource, MapMouseEvent, MapGeoJSONFeature } from 'maplibre-gl';
import { mapEngine, safeIsStyleLoaded, isMapReadyForSources } from '../map-engine/MapEngineService';
import { census2022Api } from './census-2022-api';
import {
  useCensusRadioLayerStore,
  type SelectedCensusRadio,
  type CensusThematicVariable,
  type CensusThematicRange,
} from './census-radio-layer-store';
import { COLOR_RAMPS, DEFAULT_SYNTAX_RAMP } from '../spatial/config/syntax-color-ramps';

// Store accessor outside React (for use inside event handlers)
const getCensusStore = () => useCensusRadioLayerStore.getState();

// ─── Layer / Source IDs ───────────────────────────────────────────────────────

const SOURCE_ID     = 'census-radios-source';
const LAYER_FILL    = 'census-radios-fill';
const LAYER_OUTLINE = 'census-radios-outline';

// ─── Paint expression helpers ─────────────────────────────────────────────────

/** Nombre de la propiedad GeoJSON (enriquecida) para cada variable temática. */
const THEMATIC_PROP: Record<CensusThematicVariable, string> = {
  population:     'populationTotalWeighted',
  pop_density_ha: '_popDensHa',
  households:     'householdsTotalWeighted',
  hh_density_ha:  '_hhDensHa',
  nbi_pct:        '_nbiPct',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildFillColor(thematic: CensusThematicVariable | null, range: CensusThematicRange | null): any {
  if (!thematic || !range) return '#2FA4B5';

  const prop  = THEMATIC_PROP[thematic];
  const ramp  = COLOR_RAMPS[DEFAULT_SYNTAX_RAMP];
  const span  = range.p95 - range.min || 1;

  return [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', prop], range.min],
    // Map [min..p95] → [0..1] using actual stop values
    ...ramp.stops.flatMap((s) => [range.min + s.stop * span, s.color]),
  ];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildFillOpacity(selectedCode: string, thematicActive: boolean): any {
  if (thematicActive) {
    return [
      'case',
      ['==', ['get', 'radioCode'], selectedCode], 0.90,
      0.70,
    ];
  }
  return [
    'case',
    ['==', ['get', 'radioCode'], selectedCode], 0.40, // seleccionado: visible
    0.07,                                              // default: casi transparente
  ];
}

// ─── Thematic range computation ───────────────────────────────────────────────

function computeP95Range(values: number[]): CensusThematicRange | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const min    = sorted[0];
  const p95Idx = Math.floor(sorted.length * 0.95);
  const p95    = sorted[Math.min(p95Idx, sorted.length - 1)];
  return { min, p95: p95 > min ? p95 : sorted[sorted.length - 1] };
}

function computeThematicRanges(
  fc: GeoJSON.FeatureCollection,
): Record<CensusThematicVariable, CensusThematicRange | null> {
  const pop:  number[] = [];
  const popD: number[] = [];
  const hh:   number[] = [];
  const hhD:  number[] = [];
  const nbi:  number[] = [];

  for (const f of fc.features) {
    const p = f.properties as Record<string, unknown> | null ?? {};
    const popVal  = p['populationTotalWeighted']  != null ? Number(p['populationTotalWeighted'])  : null;
    const hhVal   = p['householdsTotalWeighted']   != null ? Number(p['householdsTotalWeighted'])  : null;
    const nbiVal  = p['householdsWithNbiWeighted'] != null ? Number(p['householdsWithNbiWeighted']) : null;
    const areaHa  = p['intersectionAreaM2']        != null && Number(p['intersectionAreaM2']) > 0
      ? Number(p['intersectionAreaM2']) / 10_000
      : null;

    if (popVal  != null) pop.push(popVal);
    if (hhVal   != null) hh.push(hhVal);
    if (popVal  != null && areaHa != null) popD.push(popVal  / areaHa);
    if (hhVal   != null && areaHa != null) hhD.push(hhVal   / areaHa);
    if (hhVal   != null && hhVal > 0 && nbiVal != null) nbi.push((nbiVal / hhVal) * 100);
  }

  return {
    population:     computeP95Range(pop),
    pop_density_ha: computeP95Range(popD),
    households:     computeP95Range(hh),
    hh_density_ha:  computeP95Range(hhD),
    nbi_pct:        computeP95Range(nbi),
  };
}

/** Enriquece el FeatureCollection con propiedades derivadas (_popDensHa, _hhDensHa, _nbiPct). */
function enrichFeatures(fc: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
  return {
    ...fc,
    features: fc.features.map((f) => {
      const p      = f.properties as Record<string, unknown> | null ?? {};
      const popVal = p['populationTotalWeighted']  != null ? Number(p['populationTotalWeighted'])  : null;
      const hhVal  = p['householdsTotalWeighted']   != null ? Number(p['householdsTotalWeighted'])  : null;
      const nbiVal = p['householdsWithNbiWeighted'] != null ? Number(p['householdsWithNbiWeighted']) : null;
      const areaM2 = p['intersectionAreaM2']        != null ? Number(p['intersectionAreaM2'])        : null;
      const areaHa = areaM2 != null && areaM2 > 0 ? areaM2 / 10_000 : null;

      return {
        ...f,
        properties: {
          ...p,
          _popDensHa: popVal != null && areaHa != null ? popVal / areaHa : null,
          _hhDensHa:  hhVal  != null && areaHa != null ? hhVal  / areaHa : null,
          _nbiPct:    hhVal  != null && hhVal > 0 && nbiVal != null ? (nbiVal / hhVal) * 100 : null,
        },
      };
    }),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildLineColor(selectedCode: string): any {
  return [
    'case',
    ['==', ['get', 'radioCode'], selectedCode], '#FFFFFF', // seleccionado: blanco
    ['==', ['get', 'isFull'], true],             '#2FA4B5', // completo: teal
    '#f59e0b',                                              // parcial: ámbar
  ];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildLineWidth(selectedCode: string): any {
  return [
    'case',
    ['==', ['get', 'radioCode'], selectedCode], 2.5, // seleccionado: grueso
    ['==', ['get', 'isFull'], true],             0.8, // completo: fino
    1.3,                                              // parcial: un poco más visible
  ];
}

function getGeomCoords(geom: GeoJSON.Geometry): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  if (geom.type === 'MultiPolygon') {
    for (const poly of geom.coordinates) {
      for (const ring of poly) {
        for (const c of ring) out.push([c[0], c[1]]);
      }
    }
  } else if (geom.type === 'Polygon') {
    for (const ring of geom.coordinates) {
      for (const c of ring) out.push([c[0], c[1]]);
    }
  }
  return out;
}

// ─── Options ──────────────────────────────────────────────────────────────────

export interface UseCensusRadioMapLayerOptions {
  projectId: string;
  visible: boolean;
  interactive: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCensusRadioMapLayer({
  projectId,
  visible,
  interactive,
}: UseCensusRadioMapLayerOptions): void {
  const layersReady          = useRef(false);
  const loadingRef           = useRef(false);
  const geojsonRef           = useRef<GeoJSON.FeatureCollection>({ type: 'FeatureCollection', features: [] });
  const visibleRef           = useRef(visible);
  const interactiveRef       = useRef(interactive);
  const lastStyleDataTimeRef = useRef<number>(0);

  const selectRadioFn      = useCensusRadioLayerStore((s) => s.selectRadio);
  const setThematicRanges  = useCensusRadioLayerStore((s) => s.setThematicRanges);
  const selectedRadio      = useCensusRadioLayerStore((s) => s.selectedRadio);
  const thematicVariable   = useCensusRadioLayerStore((s) => s.thematicVariable);
  const thematicRanges     = useCensusRadioLayerStore((s) => s.thematicRanges);
  const selectRadioRef     = useRef(selectRadioFn);
  const setThematicRef     = useRef(setThematicRanges);

  // ── Mantener refs sincronizados ───────────────────────────────────────────
  useEffect(() => { selectRadioRef.current  = selectRadioFn;     }, [selectRadioFn]);
  useEffect(() => { setThematicRef.current  = setThematicRanges; }, [setThematicRanges]);
  useEffect(() => { visibleRef.current      = visible;           }, [visible]);
  useEffect(() => { interactiveRef.current  = interactive;       }, [interactive]);

  // ── Reset cursor cuando interactive se apaga ──────────────────────────────
  useEffect(() => {
    if (interactive) return;
    const map = mapEngine.getMap();
    if (map) map.getCanvas().style.cursor = '';
  }, [interactive]);

  // ── Cargar GeoJSON del proyecto ───────────────────────────────────────────
  const loadGeoJSON = useCallback(async (): Promise<void> => {
    if (!projectId || loadingRef.current) return;
    loadingRef.current = true;
    try {
      const raw = await census2022Api.getRadiosGeoJSON(projectId);
      const enriched = enrichFeatures(raw as GeoJSON.FeatureCollection);
      geojsonRef.current = enriched;

      // Compute thematic ranges and expose to store
      const ranges = computeThematicRanges(enriched);
      setThematicRef.current(ranges);

      const map = mapEngine.getMap();
      if (map && layersReady.current) {
        const src = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
        if (src) src.setData(enriched);
      }
    } catch (err) {
      console.error('[useCensusRadioMapLayer] Error cargando GeoJSON:', err);
    } finally {
      loadingRef.current = false;
    }
  }, [projectId]);

  // ── Actualizar estilos cuando cambia radio seleccionado o variable temática ──
  useEffect(() => {
    if (!layersReady.current) return;
    const map = mapEngine.getMap();
    if (!map) return;
    const code = selectedRadio?.radioCode ?? '';
    const range = thematicVariable ? thematicRanges[thematicVariable] : null;
    if (map.getLayer(LAYER_FILL)) {
      map.setPaintProperty(LAYER_FILL, 'fill-color',   buildFillColor(thematicVariable, range));
      map.setPaintProperty(LAYER_FILL, 'fill-opacity', buildFillOpacity(code, thematicVariable !== null));
    }
    if (map.getLayer(LAYER_OUTLINE)) {
      map.setPaintProperty(LAYER_OUTLINE, 'line-color', buildLineColor(code));
      map.setPaintProperty(LAYER_OUTLINE, 'line-width', buildLineWidth(code));
    }
  }, [selectedRadio, thematicVariable, thematicRanges]);

  // ── Setup principal + listeners ───────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    const map = mapEngine.getMap();
    if (!map) return;

    // ── Agregar source + layers (idempotente) ─────────────────────────────
    const setup = (): void => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: geojsonRef.current,
        });
      }
      if (!map.getLayer(LAYER_FILL)) {
        map.addLayer({
          id: LAYER_FILL,
          type: 'fill',
          source: SOURCE_ID,
          paint: {
            'fill-color':   buildFillColor(null, null),
            'fill-opacity': buildFillOpacity('', false),
          },
        });
      }
      if (!map.getLayer(LAYER_OUTLINE)) {
        map.addLayer({
          id: LAYER_OUTLINE,
          type: 'line',
          source: SOURCE_ID,
          paint: {
            'line-color': buildLineColor(''),
            'line-width': buildLineWidth(''),
          },
        });
      }
      // Aplicar visibilidad actual
      const vis = visibleRef.current ? 'visible' : 'none';
      if (map.getLayer(LAYER_FILL))    map.setLayoutProperty(LAYER_FILL,    'visibility', vis);
      if (map.getLayer(LAYER_OUTLINE)) map.setLayoutProperty(LAYER_OUTLINE, 'visibility', vis);

      layersReady.current = true;
      console.log('[useCensusRadioMapLayer] Layers listas');
    };

    // ── Handlers con guards de interactividad ─────────────────────────────
    const handleClick = (
      e: MapMouseEvent & { features?: MapGeoJSONFeature[] },
    ): void => {
      if (!interactiveRef.current) return; // power OFF → ignorar
      if (!e.features?.length) return;
      const p = (e.features[0].properties ?? {}) as Record<string, unknown>;
      const radio: SelectedCensusRadio = {
        radioCode:                 String(p['radioCode'] ?? ''),
        departmentName:            (p['departmentName'] as string | null) ?? null,
        provinceName:              (p['provinceName']   as string | null) ?? null,
        coverageRatio:             Number(p['coverageRatio'] ?? 0),
        isFull:                    Boolean(p['isFull']),
        hasIndicators:             Boolean(p['hasIndicators']),
        populationTotalWeighted:   p['populationTotalWeighted']   != null ? Number(p['populationTotalWeighted'])   : null,
        householdsTotalWeighted:   p['householdsTotalWeighted']   != null ? Number(p['householdsTotalWeighted'])   : null,
        householdsWithNbiWeighted: p['householdsWithNbiWeighted'] != null ? Number(p['householdsWithNbiWeighted']) : null,
        radioAreaM2:               p['radioAreaM2']               != null ? Number(p['radioAreaM2'])               : null,
        intersectionAreaM2:        p['intersectionAreaM2']        != null ? Number(p['intersectionAreaM2'])        : null,
      };
      selectRadioRef.current(radio);
    };

    const handleMouseEnter = (): void => {
      if (!interactiveRef.current) return; // power OFF → sin cursor
      map.getCanvas().style.cursor = 'pointer';
    };

    const handleMouseLeave = (): void => {
      if (!interactiveRef.current) return;
      map.getCanvas().style.cursor = '';
    };

    // ── Zoom desde inventario (siempre activo, no requiere power) ─────────
    const handleZoom = (e: Event): void => {
      const { radioCode } = (e as CustomEvent<{ radioCode: string }>).detail ?? {};
      console.log('[CensusRadioZoom] requested', radioCode);
      if (!radioCode) return;

      const feature = geojsonRef.current.features.find(
        (f) => f.properties?.['radioCode'] === radioCode,
      );
      console.log('[CensusRadioZoom] feature found?', !!feature, '(total loaded:', geojsonRef.current.features.length, ')');

      if (!feature?.geometry) {
        // GeoJSON still loading — trigger load and bail; user can retry
        if (!loadingRef.current) void loadGeoJSON();
        return;
      }

      const m = mapEngine.getMap();
      if (!m) return;

      // Auto-enable visible so user can see the result
      const store = getCensusStore();
      if (!store.visible) store.setVisible(true);

      const coords = getGeomCoords(feature.geometry as GeoJSON.Geometry);
      if (!coords.length) return;

      const lngs   = coords.map((c) => c[0]);
      const lats   = coords.map((c) => c[1]);
      const bounds: [[number, number], [number, number]] = [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ];
      console.log('[CensusRadioZoom] bounds', bounds);
      m.fitBounds(bounds, { padding: 80, maxZoom: 16, duration: 700 });
    };

    map.on('click',      LAYER_FILL, handleClick);
    map.on('mouseenter', LAYER_FILL, handleMouseEnter);
    map.on('mouseleave', LAYER_FILL, handleMouseLeave);
    window.addEventListener('census-radio-zoom', handleZoom);

    // ── Re-inyectar layers si el style se reinicia ────────────────────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleStyleData = (e: any): void => {
      if (e.dataType !== 'style') return;
      const now = Date.now();
      if (now - lastStyleDataTimeRef.current < 1000) return;
      lastStyleDataTimeRef.current = now;
      if (map.getSource(SOURCE_ID)) return; // source sobrevivió
      layersReady.current = false;
      window.setTimeout(() => {
        setup();
        const src = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
        if (src) src.setData(geojsonRef.current);
      }, 150);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.on('styledata', handleStyleData as any);

    // ── Boot ──────────────────────────────────────────────────────────────
    const boot = (): void => {
      setup();
      void loadGeoJSON(); // carga siempre → zoom funciona aunque capa esté oculta
    };

    if (safeIsStyleLoaded(map)) {
      boot();
    } else {
      let settled = false;
      map.once('load', () => { if (!settled) { settled = true; boot(); } });
      const poll = setInterval(() => {
        if (safeIsStyleLoaded(map) && !settled) { settled = true; clearInterval(poll); boot(); }
      }, 200);
      setTimeout(() => clearInterval(poll), 10_000);
    }

    return (): void => {
      const m = mapEngine.getMap();
      if (m) {
        m.off('click',      LAYER_FILL, handleClick);
        m.off('mouseenter', LAYER_FILL, handleMouseEnter);
        m.off('mouseleave', LAYER_FILL, handleMouseLeave);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        m.off('styledata', handleStyleData as any);
        if (isMapReadyForSources(m)) {
          try { if (m.getLayer(LAYER_OUTLINE)) m.removeLayer(LAYER_OUTLINE); } catch { /* ignore */ }
          try { if (m.getLayer(LAYER_FILL))    m.removeLayer(LAYER_FILL);    } catch { /* ignore */ }
          try { if (m.getSource(SOURCE_ID))    m.removeSource(SOURCE_ID);    } catch { /* ignore */ }
        }
      }
      window.removeEventListener('census-radio-zoom', handleZoom);
      layersReady.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, loadGeoJSON]);

  // ── Eye toggle: visibilidad sin destruir source ───────────────────────────
  useEffect(() => {
    if (!layersReady.current) return;
    const map = mapEngine.getMap();
    if (!map) return;
    const vis = visible ? 'visible' : 'none';
    if (map.getLayer(LAYER_FILL))    map.setLayoutProperty(LAYER_FILL,    'visibility', vis);
    if (map.getLayer(LAYER_OUTLINE)) map.setLayoutProperty(LAYER_OUTLINE, 'visibility', vis);
  }, [visible]);
}
